import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Project,
  DeploymentEnvironmentVariable,
  Database as DatabaseType,
  ProjectDeploymentSummary,
} from '@claude-code-api/shared';
import { RESERVED_ENV_KEYS } from '@claude-code-api/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Settings,
  GitBranch,
  Copy,
  Database,
  ChevronDown,
  Package,
  Tag,
} from 'lucide-react';

type BuildInfo = {
  id: number;
  title: string;
  status: string;
  commit_sha: string;
  prettyref: string;
  created: string;
  updated: string;
  started: string;
  stopped: string;
  event: string;
  html_url: string;
};

export type DeploymentInfo = ProjectDeploymentSummary;

type ImageInfo = {
  id: number;
  version: string;
  created_at: string;
  packageName?: string;
  metadata: { container?: { tags: string[] } };
};

interface ProjectDeploymentsTabProps {
  project: Project;
}

export function ProjectDeploymentsTab({ project }: ProjectDeploymentsTabProps) {
  const { t, i18n } = useTranslation(['projects', 'common']);
  // CI/CD state
  const [builds, setBuilds] = useState<BuildInfo[]>([]);
  const [loadingBuilds, setLoadingBuilds] = useState(false);
  const [images, setImages] = useState<ImageInfo[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedBuild, setSelectedBuild] = useState<BuildInfo | null>(null);

  // Deployment state
  const [deployments, setDeployments] = useState<DeploymentInfo[]>([]);
  const [loadingDeployments, setLoadingDeployments] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [deploySubdomain, setDeploySubdomain] = useState('');
  const [deployVersion, setDeployVersion] = useState('latest');
  const [deployDatabaseOption, setDeployDatabaseOption] = useState<'none' | 'new' | 'existing'>('none');
  const [deployExistingDatabaseId, setDeployExistingDatabaseId] = useState<string>('');
  const [userDatabases, setUserDatabases] = useState<DatabaseType[]>([]);
  const [deploying, setDeploying] = useState(false);
  const [deploymentToDelete, setDeploymentToDelete] = useState<string | null>(null);
  const [deletingDeployment, setDeletingDeployment] = useState(false);
  const [deployEnvVars, setDeployEnvVars] = useState<DeploymentEnvironmentVariable[]>([]);
  const [deployCpuFrontend, setDeployCpuFrontend] = useState(0.5);
  const [deployMemFrontend, setDeployMemFrontend] = useState(0.5);
  const [deployCpuBackend, setDeployCpuBackend] = useState(1);
  const [deployMemBackend, setDeployMemBackend] = useState(1);
  const [deployStorageGb, setDeployStorageGb] = useState(0);
  const [resourcePool, setResourcePool] = useState<{ available: { cpuCores: number; memoryGb: number; storageGb: number } } | null>(null);

  // Env var editing modal state
  const [envEditDeploymentId, setEnvEditDeploymentId] = useState<string | null>(null);
  const [envEditVars, setEnvEditVars] = useState<DeploymentEnvironmentVariable[]>([]);
  const [envEditLoading, setEnvEditLoading] = useState(false);
  const [envEditSaving, setEnvEditSaving] = useState(false);
  const [envEditApplying, setEnvEditApplying] = useState(false);
  const [expandedImageGroup, setExpandedImageGroup] = useState<string | null>(null);

  useEffect(() => {
    if (project.hasForgejoRepo && !project.gitUrl) {
      fetchBuilds();
      fetchImages();
      if (project.templateId) {
        fetchDeployments();
      }
    }
  }, [project.id, project.hasForgejoRepo, project.gitUrl, project.templateId]);

  if (!project.hasForgejoRepo || project.gitUrl) {
    return (
      <div className="text-center py-12">
        <GitBranch className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-1">{t('projects:deployments.emptyCicdTitle')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('projects:deployments.emptyCicdDescription')}
        </p>
      </div>
    );
  }

  const fetchBuilds = async () => {
    setLoadingBuilds(true);
    try {
      const response = await fetch(`/api/forgejo/projects/${project.id}/builds`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setBuilds(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch builds:', error);
      toast.error(t('projects:deployments.errors.loadBuilds'));
    } finally {
      setLoadingBuilds(false);
    }
  };

  const fetchImages = async () => {
    setLoadingImages(true);
    try {
      const response = await fetch(`/api/forgejo/projects/${project.id}/images`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setImages(data.data.versions || []);
      }
    } catch (error) {
      console.error('Failed to fetch images:', error);
      toast.error(t('projects:deployments.errors.loadImages'));
    } finally {
      setLoadingImages(false);
    }
  };

  const fetchDeployments = async () => {
    setLoadingDeployments(true);
    try {
      const response = await fetch(`/api/projects/${project.id}/deployments`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setDeployments(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch deployments:', error);
      toast.error(t('projects:deployments.errors.loadDeployments'));
    } finally {
      setLoadingDeployments(false);
    }
  };

  const fetchUserDatabases = async () => {
    try {
      const response = await fetch('/api/databases', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUserDatabases(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch user databases:', error);
    }
  };

  const fetchResourcePool = async () => {
    try {
      const res = await fetch('/api/subscriptions/resources', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setResourcePool(data.data);
      }
    } catch {
      // silently fail
    }
  };

  const createDeployment = async () => {
    if (!deploySubdomain) return;
    setDeploying(true);
    try {
      const response = await fetch(`/api/projects/${project.id}/deployments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          imageVersion: deployVersion,
          subdomain: deploySubdomain,
          createDatabase: deployDatabaseOption === 'new',
          existingDatabaseId: deployDatabaseOption === 'existing' ? deployExistingDatabaseId : undefined,
          environmentVariables: deployEnvVars.length > 0 ? deployEnvVars : undefined,
          cpuCoresFrontend: deployCpuFrontend,
          memoryGbFrontend: deployMemFrontend,
          cpuCoresBackend: deployCpuBackend,
          memoryGbBackend: deployMemBackend,
          storageGb: deployStorageGb,
        }),
      });
      if (response.ok) {
        setShowDeployModal(false);
        setDeploySubdomain('');
        setDeployVersion('latest');
        setDeployDatabaseOption('none');
        setDeployExistingDatabaseId('');
        setDeployEnvVars([]);
        setDeployCpuFrontend(0.5);
        setDeployMemFrontend(0.5);
        setDeployCpuBackend(1);
        setDeployMemBackend(1);
        setDeployStorageGb(0);
        fetchDeployments();
      } else {
        const error = await response.json();
        toast.error(error.error || t('projects:deployments.errors.create'));
      }
    } catch (error) {
      console.error('Failed to create deployment:', error);
      toast.error(t('projects:deployments.errors.create'));
    } finally {
      setDeploying(false);
    }
  };

  const stopDeployment = async (id: string) => {
    try {
      await fetch(`/api/deployments/${id}/stop`, { method: 'POST', credentials: 'include' });
      fetchDeployments();
    } catch (error) {
      console.error('Failed to stop deployment:', error);
      toast.error(t('projects:deployments.errors.stop'));
    }
  };

  const restartDeployment = async (id: string) => {
    try {
      await fetch(`/api/deployments/${id}/restart`, { method: 'POST', credentials: 'include' });
      fetchDeployments();
    } catch (error) {
      console.error('Failed to restart deployment:', error);
      toast.error(t('projects:deployments.errors.restart'));
    }
  };

  const deleteDeployment = async () => {
    if (!deploymentToDelete) return;
    setDeletingDeployment(true);
    try {
      await fetch(`/api/deployments/${deploymentToDelete}`, { method: 'DELETE', credentials: 'include' });
      setDeploymentToDelete(null);
      fetchDeployments();
    } catch (error) {
      console.error('Failed to delete deployment:', error);
      toast.error(t('projects:deployments.errors.delete'));
    } finally {
      setDeletingDeployment(false);
    }
  };

  const openEnvEditor = async (deploymentId: string) => {
    setEnvEditDeploymentId(deploymentId);
    setEnvEditLoading(true);
    try {
      const response = await fetch(`/api/deployments/${deploymentId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEnvEditVars(data.data.environmentVariables || []);
      }
    } catch (error) {
      console.error('Failed to fetch deployment env vars:', error);
      toast.error(t('projects:deployments.errors.loadEnvVars'));
    } finally {
      setEnvEditLoading(false);
    }
  };

  const saveEnvVars = async () => {
    if (!envEditDeploymentId) return;
    setEnvEditSaving(true);
    try {
      const response = await fetch(`/api/deployments/${envEditDeploymentId}/environment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ environmentVariables: envEditVars }),
      });
      if (response.ok) {
        toast.success(t('projects:deployments.messages.envSaved'));
        fetchDeployments();
      } else {
        const error = await response.json();
        toast.error(error.error || t('projects:deployments.errors.saveEnvVars'));
      }
    } catch (error) {
      console.error('Failed to save env vars:', error);
      toast.error(t('projects:deployments.errors.saveEnvVars'));
    } finally {
      setEnvEditSaving(false);
    }
  };

  const applyEnvVars = async () => {
    if (!envEditDeploymentId) return;
    setEnvEditApplying(true);
    try {
      const saveResponse = await fetch(`/api/deployments/${envEditDeploymentId}/environment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ environmentVariables: envEditVars }),
      });
      if (!saveResponse.ok) {
        const error = await saveResponse.json();
        toast.error(error.error || t('projects:deployments.errors.saveEnvVars'));
        return;
      }
      const applyResponse = await fetch(`/api/deployments/${envEditDeploymentId}/apply-environment`, {
        method: 'POST',
        credentials: 'include',
      });
      if (applyResponse.ok) {
        toast.success(t('projects:deployments.messages.envApplied'));
        setEnvEditDeploymentId(null);
        fetchDeployments();
      } else {
        const error = await applyResponse.json();
        toast.error(error.error || t('projects:deployments.errors.applyEnv'));
      }
    } catch (error) {
      console.error('Failed to apply env vars:', error);
      toast.error(t('projects:deployments.errors.applyEnv'));
    } finally {
      setEnvEditApplying(false);
    }
  };

  const renderEnvVarEditor = (
    envVars: DeploymentEnvironmentVariable[],
    setEnvVars: (vars: DeploymentEnvironmentVariable[]) => void,
  ) => (
      <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{t('projects:deployments.envVars')}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setEnvVars([...envVars, { key: '', value: '', target: 'backend' }])}
        >
          <Plus className="w-3 h-3 mr-1" /> {t('projects:deployments.addVariable')}
        </Button>
      </div>
      {envVars.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">{t('projects:deployments.noEnvVars')}</p>
      ) : (
        <div className="space-y-2">
          {envVars.map((envVar, index) => {
            const isReserved = RESERVED_ENV_KEYS.has(envVar.key.trim().toUpperCase());
            return (
              <div key={index} className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <Input
                    placeholder={t('projects:deployments.keyPlaceholder')}
                    value={envVar.key}
                    onChange={(e) => {
                      const updated = [...envVars];
                      updated[index] = { ...envVar, key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '').replace(/^[^A-Z]+/, '') };
                      setEnvVars(updated);
                    }}
                  className={`font-mono text-xs ${isReserved ? 'border-destructive' : ''}`}
                />
                {isReserved && (
                    <p className="text-xs text-destructive mt-0.5">{t('projects:deployments.reservedKey', { key: envVar.key })}</p>
                )}
              </div>
                <Input
                  placeholder={t('projects:deployments.valuePlaceholder')}
                  value={envVar.value}
                  onChange={(e) => {
                    const updated = [...envVars];
                    updated[index] = { ...envVar, value: e.target.value };
                    setEnvVars(updated);
                  }}
                  className="flex-1 min-w-0 font-mono text-xs"
                  type={envVar.secret ? 'password' : 'text'}
                />
                <select
                  value={envVar.target}
                  onChange={(e) => {
                    const updated = [...envVars];
                    updated[index] = { ...envVar, target: e.target.value as 'frontend' | 'backend' | 'both' };
                    setEnvVars(updated);
                  }}
                  className="h-10 px-2 rounded-md border border-input bg-background text-xs w-[100px]"
                >
                  <option value="backend">{t('projects:deployments.targets.backend')}</option>
                  <option value="frontend">{t('projects:deployments.targets.frontend')}</option>
                  <option value="both">{t('projects:deployments.targets.both')}</option>
                </select>
                <label className="flex items-center gap-1 h-10 text-xs text-muted-foreground cursor-pointer whitespace-nowrap">
                  <Checkbox
                    checked={envVar.secret || false}
                    onCheckedChange={(checked) => {
                      const updated = [...envVars];
                      updated[index] = { ...envVar, secret: checked === true };
                      setEnvVars(updated);
                    }}
                  />
                  {t('projects:deployments.secret')}
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-10 w-10 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => {
                    const updated = envVars.filter((_, i) => i !== index);
                    setEnvVars(updated);
                  }}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <Tabs defaultValue={project.templateId ? 'deployments' : 'builds'}>
        <TabsList>
          {project.templateId && <TabsTrigger value="deployments">{t('projects:detail.tabs.deployments')}</TabsTrigger>}
          <TabsTrigger value="builds">{t('projects:deployments.tabs.builds')}</TabsTrigger>
          <TabsTrigger value="images">{t('projects:deployments.tabs.images')}</TabsTrigger>
        </TabsList>

        {/* Deployments Sub-tab */}
        {project.templateId && (
          <TabsContent value="deployments" className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-muted-foreground">
                {t('projects:deployments.deploymentCount', { count: deployments.length })}
              </h3>
              <Button
                size="sm"
                onClick={() => { fetchUserDatabases(); setShowDeployModal(true); }}
              >
                <Plus className="w-3 h-3 mr-1" /> {t('projects:deployments.deploy')}
              </Button>
            </div>

            {loadingDeployments ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : deployments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('projects:deployments.noDeployments')}
              </p>
            ) : (
              <div className="space-y-3">
                {deployments.map((deployment) => (
                  <Card key={deployment.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <StatusIndicator
                              status={
                                deployment.containerStatus.frontend === 'running' &&
                                deployment.containerStatus.backend === 'running'
                                  ? 'running'
                                  : deployment.status === 'failed'
                                  ? 'error'
                                  : 'stopped'
                              }
                            />
                            <span className="font-medium">{deployment.subdomain}-app</span>
                            <Badge variant="secondary" className="text-xs">
                              v{deployment.imageVersion}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <a
                              href={`https://${deployment.subdomain}-app.${window.location.hostname}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline text-primary"
                            >
                              {t('projects:deployments.openApp')}
                            </a>
                            <span>{t('projects:deployments.created', { date: new Intl.DateTimeFormat(i18n.language).format(new Date(deployment.createdAt)) })}</span>
                            {deployment.environmentVariableCount > 0 && (
                              <span>{t('projects:deployments.envVarCount', { count: deployment.environmentVariableCount })}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => openEnvEditor(deployment.id)}>
                            <Settings className="w-3 h-3 mr-1" /> {t('projects:deployments.env')}
                          </Button>
                          {deployment.containerStatus.frontend === 'running' ? (
                            <Button variant="outline" size="sm" onClick={() => stopDeployment(deployment.id)}>
                              {t('projects:sessions.stop')}
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => restartDeployment(deployment.id)}>
                              {t('projects:deployments.start')}
                            </Button>
                          )}
                          <Button variant="destructive" size="sm" onClick={() => setDeploymentToDelete(deployment.id)} aria-label={t('projects:deployments.deleteDeployment')}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* Builds Sub-tab */}
        <TabsContent value="builds" className="mt-4">
          {loadingBuilds ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : builds.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {t('projects:deployments.noBuilds')}
            </p>
          ) : (
            <div className="space-y-2">
              {[...builds].sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()).map((build) => (
                <div
                  key={build.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <StatusIndicator
                      status={
                        build.status === 'success' ? 'success' :
                        build.status === 'failure' ? 'error' :
                        build.status === 'running' ? 'running' :
                        'pending'
                      }
                    />
                    <div>
                      <p className="text-sm font-medium">
                        {build.prettyref || t('projects:deployments.mainBranch')}
                        <span className="ml-2 text-xs font-mono text-muted-foreground">
                          {build.commit_sha?.substring(0, 7)}
                        </span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(build.created))}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedBuild(build)}
                  >
                    {t('projects:deployments.details')}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Images Sub-tab */}
        <TabsContent value="images" className="mt-4">
          {loadingImages ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : images.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {t('projects:deployments.noImages')}
            </p>
          ) : (() => {
            const filtered = images.filter((img) => !img.version.startsWith('sha256:') && img.version !== 'buildcache');
            const grouped = filtered.reduce<Record<string, ImageInfo[]>>((acc, img) => {
              const key = img.packageName?.split('/').pop() || 'unknown';
              if (!acc[key]) acc[key] = [];
              acc[key].push(img);
              return acc;
            }, {});
            Object.values(grouped).forEach((arr) => arr.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));

            return Object.keys(grouped).length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('projects:deployments.noTaggedImages')}
              </p>
            ) : (
              <div className="grid gap-4 grid-cols-1">
                {Object.entries(grouped).map(([name, versions]) => {
                  const isExpanded = expandedImageGroup === name;
                  const latestVersion = versions[0];
                  const displayVersions = isExpanded ? versions.slice(0, 3) : [];

                  return (
                    <div
                      key={name}
                      className="bg-card border rounded-lg p-4 cursor-pointer hover:bg-accent/50 transition-colors duration-150"
                      onClick={() => setExpandedImageGroup(isExpanded ? null : name)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <h3 className="font-semibold truncate">{name}</h3>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge variant="outline" className="text-xs">
                            {t('projects:deployments.versionCount', { count: versions.length })}
                          </Badge>
                          <ChevronDown
                            className={`w-4 h-4 text-muted-foreground transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground ml-6">
                        {t('projects:deployments.latestVersion', { version: latestVersion.version, date: new Intl.DateTimeFormat(i18n.language).format(new Date(latestVersion.created_at)) })}
                      </p>

                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t space-y-2" onClick={(e) => e.stopPropagation()}>
                          {displayVersions.map((image) => {
                            const registryUrl = project.forgejoRepoUrl?.replace('https://', '').replace('.git', '');
                            const tag = image.metadata?.container?.tags?.[0] || image.version;
                            const pullCmd = `docker pull ${registryUrl}/${name}:${tag}`;

                            return (
                              <div
                                key={image.id}
                                className="flex items-center justify-between gap-2 p-2 rounded border bg-background"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <Tag className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                    <span className="font-mono text-sm truncate">{image.version}</span>
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-0.5 ml-5">
                                    {new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(image.created_at))}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    try { navigator.clipboard.writeText(pullCmd); toast.success(t('projects:deployments.pullCopied')); } catch { /* ignore */ }
                                  }}
                                >
                                  <Copy className="w-3 h-3 mr-1" /> {t('projects:deployments.pull')}
                                </Button>
                              </div>
                            );
                          })}
                          {versions.length > 3 && (
                            <p className="text-xs text-muted-foreground text-center pt-1">
                              {t('projects:deployments.olderVersions', { count: versions.length - 3 })}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>

      {/* Build Details Modal */}
      <Dialog open={selectedBuild !== null} onOpenChange={(open) => !open && setSelectedBuild(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('projects:deployments.buildDetails')}</DialogTitle>
          </DialogHeader>
          {selectedBuild && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <StatusIndicator
                  status={
                    selectedBuild.status === 'success' ? 'success' :
                    selectedBuild.status === 'failure' ? 'error' :
                    selectedBuild.status === 'running' ? 'running' :
                    'pending'
                  }
                />
                <span className="font-medium capitalize">{selectedBuild.status}</span>
              </div>
              <div className="grid gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('projects:deployments.fields.branch')}</span>
                  <span className="font-mono">{selectedBuild.prettyref || t('projects:deployments.mainBranch')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('projects:deployments.fields.commit')}</span>
                  <span className="font-mono">{selectedBuild.commit_sha?.substring(0, 12)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('projects:deployments.fields.event')}</span>
                  <span>{selectedBuild.event}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('projects:deployments.fields.created')}</span>
                  <span>{new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(selectedBuild.created))}</span>
                </div>
                {selectedBuild.started && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('projects:deployments.fields.started')}</span>
                    <span>{new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(selectedBuild.started))}</span>
                  </div>
                )}
                {selectedBuild.stopped && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('projects:deployments.fields.finished')}</span>
                    <span>{new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(selectedBuild.stopped))}</span>
                  </div>
                )}
                {selectedBuild.started && selectedBuild.stopped && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('projects:deployments.fields.duration')}</span>
                    <span>{t('projects:deployments.durationSeconds', { count: Math.round((new Date(selectedBuild.stopped).getTime() - new Date(selectedBuild.started).getTime()) / 1000) })}</span>
                  </div>
                )}
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
                <p>{t('projects:deployments.noDetailedLogs')}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deploy Modal */}
      <Dialog open={showDeployModal} onOpenChange={(open) => { setShowDeployModal(open); if (open) fetchResourcePool(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('projects:deployments.deployApplication')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="subdomain">{t('projects:deployments.subdomain')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="subdomain"
                  placeholder={t('projects:deployments.subdomainPlaceholder')}
                  value={deploySubdomain}
                  onChange={(e) => setDeploySubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  className="flex-1"
                />
                <span className="text-muted-foreground">-app.{window.location.hostname}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('projects:deployments.appAvailableAt', { subdomain: deploySubdomain || t('projects:deployments.subdomainPlaceholder'), host: window.location.hostname })}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="version">{t('projects:deployments.imageVersion')}</Label>
              <select
                id="version"
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={deployVersion}
                onChange={(e) => setDeployVersion(e.target.value)}
              >
                <option value="latest">{t('projects:deployments.latest')}</option>
                {images
                  .filter((img) => img.version !== 'latest' && !img.version.startsWith('sha256:') && img.version !== 'buildcache')
                  .map((img) => (
                    <option key={img.id} value={img.version}>
                      {img.version}
                    </option>
                  ))}
              </select>
            </div>
            {renderEnvVarEditor(deployEnvVars, setDeployEnvVars)}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Database className="w-4 h-4 text-success" />
                {t('projects:database.fields.database')}
              </Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={deployDatabaseOption}
                onChange={(e) => {
                  const val = e.target.value as 'none' | 'new' | 'existing';
                  setDeployDatabaseOption(val);
                  if (val !== 'existing') setDeployExistingDatabaseId('');
                }}
              >
                <option value="none">{t('projects:deployments.database.none')}</option>
                <option value="new">{t('projects:deployments.database.createNew')}</option>
                <option value="existing">{t('projects:deployments.database.useExisting')}</option>
              </select>
              {deployDatabaseOption === 'new' && (
                <p className="text-xs text-muted-foreground">
                  {t('projects:deployments.database.newHelp')}
                </p>
              )}
              {deployDatabaseOption === 'existing' && (
                <div className="space-y-2">
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={deployExistingDatabaseId}
                    onChange={(e) => setDeployExistingDatabaseId(e.target.value)}
                  >
                    <option value="">{t('projects:deployments.database.select')}</option>
                    {userDatabases.map((db) => (
                      <option key={db.id} value={db.id}>
                        {db.name}
                      </option>
                    ))}
                  </select>
                  {userDatabases.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      {t('projects:deployments.database.noneFound')}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {t('projects:deployments.database.existingHelp')}
                  </p>
                </div>
              )}
            </div>
            {/* Resource allocation */}
            <div className="space-y-3">
              <Label>{t('projects:deployments.resourceAllocation')}</Label>
              {resourcePool && (
                <p className="text-xs text-muted-foreground">
                  {t('projects:deployments.availableResources', resourcePool.available)}
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">{t('projects:deployments.backendCpu')}</label>
                  <Input type="number" min={0.25} step={0.25} value={deployCpuBackend} onChange={(e) => setDeployCpuBackend(Number(e.target.value))} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">{t('projects:deployments.backendRam')}</label>
                  <Input type="number" min={0.25} step={0.25} value={deployMemBackend} onChange={(e) => setDeployMemBackend(Number(e.target.value))} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">{t('projects:deployments.frontendCpu')}</label>
                  <Input type="number" min={0.25} step={0.25} value={deployCpuFrontend} onChange={(e) => setDeployCpuFrontend(Number(e.target.value))} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">{t('projects:deployments.frontendRam')}</label>
                  <Input type="number" min={0.25} step={0.25} value={deployMemFrontend} onChange={(e) => setDeployMemFrontend(Number(e.target.value))} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">{t('projects:deployments.storageGb')}</label>
                <Input type="number" min={0} step={1} value={deployStorageGb} onChange={(e) => setDeployStorageGb(Number(e.target.value))} />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('projects:deployments.totalResources', { cpu: deployCpuFrontend + deployCpuBackend, memory: deployMemFrontend + deployMemBackend, storage: deployStorageGb })}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeployModal(false)}>
              {t('common:cancel')}
            </Button>
            <Button onClick={createDeployment} disabled={deploying || !deploySubdomain || (deployDatabaseOption === 'existing' && !deployExistingDatabaseId)}>
              {deploying ? t('projects:deployments.deploying') : t('projects:deployments.deploy')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Deployment Confirmation */}
      <ConfirmDialog
        open={deploymentToDelete !== null}
        onOpenChange={(open) => { if (!open) setDeploymentToDelete(null); }}
        title={t('projects:deployments.deleteDeploymentTitle')}
        description={t('projects:deployments.deleteDeploymentDescription')}
        confirmLabel={t('projects:deployments.deleteDeployment')}
        cancelLabel={t('common:cancel')}
        onConfirm={deleteDeployment}
        loading={deletingDeployment}
        variant="destructive"
      />

      {/* Edit Environment Variables Modal */}
      <Dialog open={envEditDeploymentId !== null} onOpenChange={(open) => { if (!open) setEnvEditDeploymentId(null); }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('projects:deployments.envVars')}</DialogTitle>
            <DialogDescription>{t('projects:deployments.envVarsDescription')}</DialogDescription>
          </DialogHeader>
          {envEditLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : (
            <>
              <div className="py-2">
                {renderEnvVarEditor(envEditVars, setEnvEditVars)}
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setEnvEditDeploymentId(null)}>
                  {t('common:cancel')}
                </Button>
                <Button variant="outline" onClick={saveEnvVars} disabled={envEditSaving || envEditApplying}>
                  {envEditSaving ? t('projects:deployments.saving') : t('common:save')}
                </Button>
                <Button onClick={applyEnvVars} disabled={envEditSaving || envEditApplying}>
                  {envEditApplying ? t('projects:deployments.applying') : t('projects:deployments.saveAndApply')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
